import Button from '@axa-japan/design-system-react/Button';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon } from '@axa-japan/design-system-react/Icons';
import RadioTileGroupField from '@axa-japan/design-system-react/RadioTileGroupField';
import Text from '@axa-japan/design-system-react/Text';
import { useState } from 'react';
import DocumentTable from '../components/DocumentTable';
import QRCodeGeneration from '../components/QRCodeGeneration';
import SMSSendingForm from '../components/SMSSendingForm';
import useCreateAccessLink from '../hooks/useCreateAccess';
import { useGetDocuments } from '../hooks/useGetDocuments';
import { queryClient } from '../libs/queryClient';
import { useUserContext } from '../providers/UserProvider';
import { AUTH_TYPE } from '../types/auth';
import { Document, DOCUMENT_UPLOAD_PROGRESS } from '../types/session';
import { ValueOf } from '../utils/fileUtils';

const EXTERNAL_UPLOAD_TYPE = {
  qrCode: 'QR_CODE',
  sms: 'SMS',
  empty: '',
} as const;

type ExternalDeviceUploadFormForm = {
  sessionId?: number;
  documentsSelected: string[];
};

type ExternalDeviceUploadFormProps = {
  initialData: ExternalDeviceUploadFormForm;
  setStepper: (step: number) => void;
  onBack: () => void;
};

const ExternalDeviceUploadForm: React.FC<ExternalDeviceUploadFormProps> = ({ initialData, onBack, setStepper }) => {
  const { connectionType, setDocumentUploadStatus } = useUserContext();
  const [uploadType, setUploadType] = useState<ValueOf<typeof EXTERNAL_UPLOAD_TYPE>>(EXTERNAL_UPLOAD_TYPE.empty);
  const [accessUrl, setAccessUrl] = useState<string | undefined>(undefined);

  const { data: documents } = useGetDocuments(initialData.sessionId as number);

  const documentToUpload = documents?.filter((document: Document) => initialData.documentsSelected.includes(document.id)) ?? [];

  const documentIds = documentToUpload.map((document) => document.id);

  const isExternal = connectionType === AUTH_TYPE.External;

  const createAccessLink = useCreateAccessLink();

  const handleChoice = async (choice: string) => {
    const isQRCode = choice === 'QR_CODE';

    if (initialData.sessionId) {
      if (isQRCode) {
        // Call API to generate the accessLink and Show the QR Code
        await createAccessLink.mutateAsync(
          {
            sessionId: initialData.sessionId,
            documentIds,
          },
          {
            onSuccess: ({ accessLink }) => {
              queryClient.invalidateQueries({ queryKey: ['session', initialData.sessionId, 'documents'] });
              if (isExternal) {
                setDocumentUploadStatus(DOCUMENT_UPLOAD_PROGRESS.completed);
                if (isQRCode) {
                  setStepper(3);
                }
              }

              setAccessUrl(accessLink);
              // Show QR code
              setUploadType(choice as ValueOf<typeof EXTERNAL_UPLOAD_TYPE>);
            },
          },
        );
      } else {
        // Show SMS sending form
        setUploadType(choice as ValueOf<typeof EXTERNAL_UPLOAD_TYPE>);
      }
    } else if (accessUrl && isQRCode && isExternal) {
      setStepper(3);
    }
  };

  return (
    <div className="flex flex-col gap-10">
      <Heading as="h1" className="pb-4">
        書類撮影をお客さまに依頼する
      </Heading>
      <div>
        <Text>お客さまのスマートフォン等で書類を送信していただきます。{isExternal && '通信料はお客様のご負担となります。'}</Text>
      </div>
      <DocumentTable onSelectRow={() => console.log()} dataTable={documentToUpload} readonly selectedRows={[]} />
      <div className="bg-statusInformationLight p-7">
        <Heading as="h3">フローの説明</Heading>
        <Text>1. お客さまにQRコードを読み取っていただく、または、SMSで届いたURLにアクセスしていただく。</Text>
        <Text>2. お客さまがスマートフォン等に表示される内容にしたがって、書類を撮影またはファイル指定して送信する。</Text>
        <Text>※お客さまに表示される画面のサンプルはこの画面右上のマニュアルで参照可能です。</Text>
      </div>
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <Heading as="h3">お客さまへのURL共有方法を選択</Heading>
          <Text>お客さまのデバイスでお手続きいただくために、URLの共有方法を選択してください。</Text>
          <RadioTileGroupField
            required={true}
            itemsPerRow={2}
            options={[
              { title: 'QRコード表示', value: EXTERNAL_UPLOAD_TYPE.qrCode },
              { title: 'SMS送信', value: EXTERNAL_UPLOAD_TYPE.sms },
            ]}
            onChange={(e) => handleChoice(e.target.value)}
          />
        </div>

        {uploadType === '' && (
          <div className="flex justify-center gap-3 mt-5">
            <div className="w-80">
              <Button variant="outlined" onClick={() => onBack()} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
                戻る
              </Button>
            </div>
          </div>
        )}

        {/* UPLOAD BY QR CODE */}
        {uploadType === EXTERNAL_UPLOAD_TYPE.qrCode && accessUrl && <QRCodeGeneration QRCodeValue={accessUrl} onBack={onBack} />}

        {/* UPLOAD BY SMS */}
        {uploadType === EXTERNAL_UPLOAD_TYPE.sms && (
          <SMSSendingForm
            onBack={onBack}
            setAccessLink={setAccessUrl}
            sessionId={Number(initialData.sessionId)}
            setStepper={setStepper}
            formIds={documentIds}
          />
        )}
      </div>
    </div>
  );
};

export default ExternalDeviceUploadForm;
